import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Subject } from 'rxjs';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
} from '@angular/material/dialog';
import { SafeHarborResultsModalData } from './models/safe-harbor-results-modal-data';
import { DraggableModalWrapperComponent } from '../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { MatButtonModule } from '@angular/material/button';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { GridHelper } from '../../../../../../core/models/grid/grid-helper';
import { SafeHarborMaximumErpExceededMappingService } from './services/safe-harbor-maximum-erp-exceeded-mapping/safe-harbor-maximum-erp-exceeded-mapping.service';
import { MaximumErpExceededRowData } from './models/maximum-erp-exceeded-row-data';
import { nameOf } from '../../../../../../core/functions/string-helpers';
import { FrequencyFormattingService } from '../../../../../../core/services/frequency-formatting/frequency-formatting.service';
import { SafeHarborResultGridApiMap } from './types/safe-harbor-result-grid-api-map';
import { SafeHarborResultRowDataMap } from './types/safe-harbor-result-row-data-map';
import { SafeHarborResultGridType } from './types/safe-harbor-result-grid-type';

@Component({
    selector: 'app-safe-harbor-results',
    standalone: true,
    imports: [
        DraggableModalWrapperComponent,
        MatButtonModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        AgGridAngular,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './safe-harbor-results.component.html',
})
export class SafeHarborResultsComponent implements OnInit, OnDestroy {
    applicationId: string = this.data.applicationId;

    destroy$ = new Subject<void>();

    gridApis: SafeHarborResultGridApiMap = {} as SafeHarborResultGridApiMap;

    gridTypes = SafeHarborResultGridType;

    rowData: SafeHarborResultRowDataMap = {
        [SafeHarborResultGridType.maximumErpExceeded]: [],
    };

    maximumErpExceededColumnDefs: ColDef[] = [
        {
            headerName: 'Location #',
            field: nameOf<MaximumErpExceededRowData>('locationNumber'),
            maxWidth: 110,
            filter: false,
            sortable: false,
        },
        {
            headerName: 'Antenna #',
            field: nameOf<MaximumErpExceededRowData>('antennaNumber'),
            maxWidth: 107,
            filter: false,
            sortable: false,
        },
        {
            headerName: 'Mobile Location #',
            field: nameOf<MaximumErpExceededRowData>('mobileLocationNumber'),
            minWidth: 164,
            filter: false,
            sortable: false,
        },
        {
            headerName: 'Frequency',
            minWidth: 189,
            valueGetter: (params) => {
                return this.frequencyFormattingService.getFrequencyLabel(
                    params.data.frequencyLow,
                    params.data.frequencyHigh,
                );
            },
        },
        {
            headerName: 'ERP',
            field: nameOf<MaximumErpExceededRowData>('erp'),
            maxWidth: 100,
            filter: false,
            sortable: false,
            pinned: 'right',
        },
        {
            headerName: 'Max ERP',
            field: nameOf<MaximumErpExceededRowData>('maximumErpAllowed'),
            maxWidth: 100,
            filter: false,
            sortable: false,
            pinned: 'right',
        },
    ];

    maximumErpExceededGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        columnDefs: this.maximumErpExceededColumnDefs,
        pagination: false,
    };

    constructor(
        @Inject(MAT_DIALOG_DATA)
        private readonly data: SafeHarborResultsModalData,
        private readonly maximumErpExceededMappingService: SafeHarborMaximumErpExceededMappingService,
        private readonly frequencyFormattingService: FrequencyFormattingService,
    ) {}

    ngOnInit(): void {
        this.rowData[SafeHarborResultGridType.maximumErpExceeded] =
            this.maximumErpExceededMappingService.getMaximumErpExceededRowData(
                this.data.checkSafeHarborResponse,
                this.data.schedulesDAndHFormValue,
            );
    }

    onGridReady(
        params: GridReadyEvent,
        type: keyof SafeHarborResultGridApiMap,
    ): void {
        this.gridApis[type] = params.api;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    passed(): boolean {
        return (
            this.rowData[SafeHarborResultGridType.maximumErpExceeded].length ===
            0
        );
    }
}
